import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isSessionError: boolean;
}

/** Patterns that indicate a broken session / missing provider context */
const SESSION_ERROR_PATTERNS = [
  'must be used within a',
  'No session found',
  'Session expired',
];

function isSessionError(error: Error): boolean {
  return SESSION_ERROR_PATTERNS.some(p => error.message.includes(p));
}

/**
 * Catches unhandled React errors in the authenticated provider tree.
 * For session-related errors (missing context providers
 * after session expiry/invalidation), forces logout and redirects to login.
 */
export class SessionErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isSessionError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, isSessionError: isSessionError(error) };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[SessionErrorBoundary] Caught error.', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    if (isSessionError(error)) {
      this.forceLogout();
    }
  }

  private forceLogout = () => {
    console.warn('[SessionErrorBoundary] Session error detected — forcing logout.');
    fetch('/api/session/logout', { method: 'POST', credentials: 'include' })
      .catch(() => { /* best effort */ })
      .finally(() => {
        try { localStorage.removeItem('session'); } catch { /* SSR / test env */ }
        window.location.href = '/';
      });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isSessionError) {
        return (
          <div className="standalone-page flex items-center justify-center min-h-[50vh] p-6">
            <p className="text-base text-muted-foreground">
              Session expired. Redirecting to login…
            </p>
          </div>
        );
      }

      return (
        <div className="standalone-page flex flex-col items-center justify-center min-h-[50vh] gap-4 p-6 text-center">
          <p className="text-base text-muted-foreground">
            Something went wrong. Please try again.
          </p>
          <button
            onClick={this.handleReload}
            className="py-2 px-6 rounded-md border-none bg-primary text-primary-foreground text-sm cursor-pointer"
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
